/**
 * Google Analytics 4 Event Tracking Utilities
 *
 * This module provides type-safe event tracking for GA4.
 * Events are only sent if user has consented to analytics cookies.
 */

// Event categories
export enum EventCategory {
  PHOTO_PROCESSING = 'photo_processing',
  USER_INTERACTION = 'user_interaction',
  ERROR = 'error',
  CONVERSION = 'conversion',
}

// Event names
export enum EventName {
  // Photo Processing Events
  PHOTO_UPLOAD = 'photo_upload',
  PHOTO_CROP = 'photo_crop',
  PHOTO_DOWNLOAD = 'photo_download',
  PROCESSING_START = 'processing_start',
  PROCESSING_COMPLETE = 'processing_complete',
  PROCESSING_ERROR = 'processing_error',

  // User Interaction Events
  COUNTRY_SELECT = 'country_select',
  LANGUAGE_CHANGE = 'language_change',
  THEME_CHANGE = 'theme_change',
  PAGE_VIEW = 'page_view',

  // Conversion Events
  PHOTO_GENERATED = 'photo_generated',
  WIZARD_COMPLETE = 'wizard_complete',

  // Error Events
  UPLOAD_ERROR = 'upload_error',
  PROCESSING_FAILED = 'processing_failed',
  NETWORK_ERROR = 'network_error',
}

export interface AnalyticsEvent {
  category: EventCategory;
  action: EventName;
  label?: string;
  value?: number;
  country?: string;
  errorMessage?: string;
  fileSize?: number;
  processingTime?: number;
}

/**
 * Send event to Google Analytics 4
 * @param event - Analytics event data
 */
export const trackEvent = (event: AnalyticsEvent): void => {
  // Only track if gtag is available and user has consented
  if (typeof window === 'undefined' || !window.gtag) {
    console.debug('GA4 not available or consent not granted');
    return;
  }

  try {
    // Send event to GA4
    window.gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      country: event.country,
      error_message: event.errorMessage,
      file_size: event.fileSize,
      processing_time: event.processingTime,
    });

    console.debug('GA4 Event tracked:', event);
  } catch (error) {
    console.error('Failed to track GA4 event:', error);
  }
};

/**
 * Track photo upload event
 * @param fileSize - Size of uploaded file in bytes
 * @param country - Selected country code
 */
export const trackPhotoUpload = (fileSize: number, country?: string): void => {
  trackEvent({
    category: EventCategory.PHOTO_PROCESSING,
    action: EventName.PHOTO_UPLOAD,
    label: country,
    fileSize,
    country,
  });
};

/**
 * Track photo crop event
 * @param country - Selected country code
 */
export const trackPhotoCrop = (country?: string): void => {
  trackEvent({
    category: EventCategory.PHOTO_PROCESSING,
    action: EventName.PHOTO_CROP,
    label: country,
    country,
  });
};

/**
 * Track photo download event
 * @param country - Selected country code
 */
export const trackPhotoDownload = (country: string): void => {
  trackEvent({
    category: EventCategory.CONVERSION,
    action: EventName.PHOTO_DOWNLOAD,
    label: country,
    country,
  });
};

/**
 * Track processing start
 * @param country - Selected country code
 */
export const trackProcessingStart = (country: string): void => {
  trackEvent({
    category: EventCategory.PHOTO_PROCESSING,
    action: EventName.PROCESSING_START,
    label: country,
    country,
  });
};

/**
 * Track processing completion
 * @param country - Selected country code
 * @param processingTime - Time taken in milliseconds
 */
export const trackProcessingComplete = (country: string, processingTime: number): void => {
  trackEvent({
    category: EventCategory.PHOTO_PROCESSING,
    action: EventName.PROCESSING_COMPLETE,
    label: country,
    country,
    processingTime,
  });
};

/**
 * Track processing error
 * @param errorMessage - Error description
 * @param country - Selected country code
 */
export const trackProcessingError = (errorMessage: string, country?: string): void => {
  trackEvent({
    category: EventCategory.ERROR,
    action: EventName.PROCESSING_ERROR,
    label: errorMessage,
    errorMessage,
    country,
  });
};

/**
 * Track country selection
 * @param country - Selected country code
 */
export const trackCountrySelect = (country: string): void => {
  trackEvent({
    category: EventCategory.USER_INTERACTION,
    action: EventName.COUNTRY_SELECT,
    label: country,
    country,
  });
};

/**
 * Track language change
 * @param language - Selected language code
 */
export const trackLanguageChange = (language: string): void => {
  trackEvent({
    category: EventCategory.USER_INTERACTION,
    action: EventName.LANGUAGE_CHANGE,
    label: language,
  });
};

/**
 * Track theme change
 * @param theme - Selected theme (light/dark)
 */
export const trackThemeChange = (theme: string): void => {
  trackEvent({
    category: EventCategory.USER_INTERACTION,
    action: EventName.THEME_CHANGE,
    label: theme,
  });
};

/**
 * Track wizard completion (full flow from upload to download)
 * @param country - Selected country code
 * @param totalTime - Total time from start to finish in milliseconds
 */
export const trackWizardComplete = (country: string, totalTime: number): void => {
  trackEvent({
    category: EventCategory.CONVERSION,
    action: EventName.WIZARD_COMPLETE,
    label: country,
    country,
    processingTime: totalTime,
  });
};

/**
 * Track upload error
 * @param errorMessage - Error description
 */
export const trackUploadError = (errorMessage: string): void => {
  trackEvent({
    category: EventCategory.ERROR,
    action: EventName.UPLOAD_ERROR,
    label: errorMessage,
    errorMessage,
  });
};
